import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, Result, Skeleton } from 'antd';
import { isNotFound } from '../../components/states';
import { PublicFormHeader, PublicFormRenderer, brandBand } from '../../components/PublicFormRenderer';
import { usePublicForm } from '../../features/forms/api';

/** Hosted form page (no sign-in). With ?embed=1 it renders bare and reports its height to the parent. */
export function PublicFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sp] = useSearchParams();
  const embed = sp.get('embed') === '1';
  const form = usePublicForm(slug);
  const ref = useRef<HTMLDivElement>(null);

  // visitors see the form's own name, without the CRM's
  const formName = form.data?.name;
  useEffect(() => {
    if (!formName) return;
    const previous = document.title;
    document.title = formName;
    return () => {
      document.title = previous;
    };
  }, [formName]);

  useEffect(() => {
    if (!embed || !ref.current) return;
    const el = ref.current;
    const post = () => window.parent?.postMessage({ type: 'crm-form-height', slug, height: el.scrollHeight + 24 }, '*');
    const ro = new ResizeObserver(post);
    ro.observe(el);
    post();
    return () => ro.disconnect();
  }, [embed, slug, form.data]);

  // the app turns overscroll off on html/body (no bounce in the shell); inside an iframe that also stops the mouse
  // wheel from scrolling the host page, so let scrolling pass through to it
  useEffect(() => {
    if (!embed) return;
    const els = [document.documentElement, document.body];
    els.forEach((e) => e.style.setProperty('overscroll-behavior', 'auto'));
    return () => els.forEach((e) => e.style.removeProperty('overscroll-behavior'));
  }, [embed]);

  const body = form.isLoading ? (
    <Skeleton active />
  ) : form.isError && !isNotFound(form.error) ? (
    <Result status="warning" title="Could not load the form" subTitle="Check your connection and try again." extra={<Button onClick={() => void form.refetch()}>Try again</Button>} />
  ) : form.isError || !form.data ? (
    <Result status="404" title="Form not found" subTitle="This form is unavailable or has been turned off." />
  ) : (
    <>
      {!embed && <PublicFormHeader form={form.data} />}
      <PublicFormRenderer form={form.data} />
    </>
  );

  if (embed) {
    return (
      // embeds sit on the host site's own page: no backdrop (the project gradient is for the hosted page)
      <div ref={ref} style={{ padding: 4 }}>
        {body}
      </div>
    );
  }
  return (
    <div style={{ minHeight: '100vh', background: form.data?.theme.background ?? '#F7F8FA', padding: '56px 16px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
      <Card style={{ width: '100%', maxWidth: 580, borderRadius: 16, ...(form.data ? brandBand(form.data) : {}) }} styles={{ body: { padding: 32 } }}>
        {body}
      </Card>
    </div>
  );
}
